import { ChangeDetectionStrategy, Component, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { SharedModule, FormInputComponent, InputComponentConfig, DynamicFormInputComponent, DataService, NotificationService } from '@vendure/admin-ui/core';
import { ParcelProfile } from '../../common/ui/generated';
import { safeJSONParse } from '../../common/ui/utils';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { GET_DEFAULT_FULFILLMENT_SETTINGS, GET_ESTIMATE_FULFILLMENT_WEIGHT_AND_COST } from './gql/order-fulfillment.graphql';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import {
    FulfillmentArgs,
    GetDefaultFulfillmentSettingsQuery,
    GetDefaultFulfillmentSettingsQueryVariables,
    GetEstimateFulfillmentWeightAndCostQuery,
    GetEstimateFulfillmentWeightAndCostQueryVariables,
    ShippingServiceType
} from './types';


const DEFAULT_MASS_UNIT = 'oz';
const DEFAULT_SHIPPING_SERVICE_TYPE = ShippingServiceType.STANDARD;
const CUSTOM_DEBOUNCE_TIME = 500; // ms
@Component({
    template: `
        <form [formGroup]="form" #formElement>
        <div>
            <vdr-form-field label="Parcel Profile">
                <vdr-dynamic-form-input #parcelProfileInput
                     class="mb-2"
                    [control]="parcelProfileControl"
                    [def]="{
                        type: 'relation',
                        entity: 'ParcelProfile',
                        ui: {
                            component: 'parcel-profile-selector',
                            options: {
                                parentPage: 'order'
                            }
                        }
                    }"
                >
                </vdr-dynamic-form-input>
            </vdr-form-field>
            <div class="mb-2">
                <div class="form-row mb-2">
                    <span>Total&nbsp;Weight</span>
                </div>
                <div *ngIf="estimatedWeight !== null"
                    class="form-row mb-1 font-italic text-muted small">
                    <strong>Estimated: {{ estimatedWeight }} {{ weightUnit }}</strong>
                </div>
                <vdr-affixed-input
                    [suffix]="weightUnit">
                    <input
                        placeholder="Or enter custom weight"
                        type="number"
                        [formControl]="customWeightControl"
                        autocomplete="off"
                    />
                </vdr-affixed-input>
            </div>
            <vdr-form-field label="Shipping Service Type">
                <select [formControl]="shippingServiceTypeControl">
                    <option value="standard">Standard</option>
                    <option value="express">Express</option>
                    <option value="next-day">Next Day</option>
                </select>
            </vdr-form-field>
            <div *ngIf="estimatedPrice !== null" class="mb-2 mt-3">
                <div class="form-row">
                    <strong>Price: {{ (estimatedPrice / 100).toFixed(2) }} USD</strong>
                </div>
            </div>
        </div>
        <div *ngIf="isLoading" class="text-muted small">Processing…</div>
        </form>
    `,
    standalone: true,
    styleUrls: ['../../common/ui/styles.scss'],
    imports: [SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FulfillmentOptionsFormComponent implements FormInputComponent<InputComponentConfig>, OnInit, AfterViewInit {
    @ViewChild('formElement') formElement: ElementRef;
    @ViewChild('parcelProfileInput') parcelProfileInput: DynamicFormInputComponent;

    readonly: boolean;
    formControl: FormControl;
    config: InputComponentConfig;

    form: FormGroup;
    parcelProfileControl = new UntypedFormControl(null, [Validators.required]);
    customWeightControl = new UntypedFormControl(null, [Validators.min(0)]);
    shippingServiceTypeControl = new UntypedFormControl(DEFAULT_SHIPPING_SERVICE_TYPE, [Validators.required]);

    orderId: string | null = null;

    isLoading = false;
    estimateError = false;

    parcelProfile: ParcelProfile | null = null;
    customWeight: number | null = null;
    weightUnit: string = DEFAULT_MASS_UNIT;
    shippingServiceType: string = DEFAULT_SHIPPING_SERVICE_TYPE;
    estimatedWeight: number | null = null;
    estimatedPrice: number | null = null;

    fulfillmentOptionsData: FulfillmentArgs;

    constructor(
        private dataService: DataService,
        private router: Router,
        private notificationService: NotificationService,
        private cdr: ChangeDetectorRef,
    ) {
        // Initialize the form group properly
        this.form = new FormGroup({
            parcelProfile: this.parcelProfileControl,
            customWeight: this.customWeightControl,
            shippingServiceType: this.shippingServiceTypeControl
        });
    }

    ngOnInit() {
        this.orderId = this.getParentPageId();
        const defaultFulfillmentOptionsData: FulfillmentArgs = {
            parcelProfileId: null,
            shippingServiceType: DEFAULT_SHIPPING_SERVICE_TYPE,
            rateId: null,
            totalWeight: null
        };
        this.fulfillmentOptionsData = safeJSONParse<FulfillmentArgs>(
            this.formControl?.value,
            this.notificationService,
            defaultFulfillmentOptionsData,
        )!;

        if (this.orderId && this.fulfillmentOptionsData.parcelProfileId === null) {
            this.getDefaultFulfillmentSettings(this.orderId);
        }

        this.customWeightControl.setValue(this.fulfillmentOptionsData.totalWeight, { emitEvent: false });
        this.shippingServiceTypeControl.setValue(this.fulfillmentOptionsData.shippingServiceType, { emitEvent: false });

        this.formControl.addValidators(() => {
            const d = this.fulfillmentOptionsData;
            const dataReady = !!(d.parcelProfileId && d.rateId && (d.totalWeight ?? this.estimatedWeight));
            return !this.isLoading && !this.estimateError && dataReady
                ? null
                : { incomplete: true };
        });
    }

    private getDefaultFulfillmentSettings(orderId: string): void {
        this.setLoading(true);
        this.dataService.query<
                GetDefaultFulfillmentSettingsQuery,
                GetDefaultFulfillmentSettingsQueryVariables
            >(GET_DEFAULT_FULFILLMENT_SETTINGS, { orderId })
            .mapSingle((data) => data.defaultFulfillmentSettings)
            .subscribe({
                next: (result) => {
                    if (!result) {
                        console.log('No defaultFulfillmentSettings found for order:', orderId);
                        this.setEstimateError(true);
                        return;
                    }
                    this.shippingServiceTypeControl.setValue(result.shippingServiceType);
                    if (result.parcelProfile) {
                        this.parcelProfileControl.setValue(result.parcelProfile, { emitEvent: false });
                        if (this.parcelProfileInput) {
                            this.parcelProfileInput.writeValue(result.parcelProfile);
                        }
                        this.fetchEstimationFulfillmentWeightAndCost(
                            result.parcelProfile.id,
                            result.shippingServiceType as ShippingServiceType,
                            this.customWeightControl.value
                        );
                    } else {
                        console.log('No default parcel profile found for order:', orderId);
                    }
                    this.setEstimateError(false);
                },
                error: (error) => {
                    console.error('Error loading default fulfillment settings:', error);
                    this.setEstimateError(true);
                    this.setLoading(false),
                    this.cdr.markForCheck();
                },
                complete: () => {
                    this.setLoading(false),
                    this.cdr.markForCheck();
                }
            });
    }

    ngAfterViewInit() {
        this.customWeightControl.valueChanges
        .pipe(
            debounceTime(CUSTOM_DEBOUNCE_TIME),
            distinctUntilChanged(),
            map(this.onCustomWeightChange)
        )
        .subscribe();
        this.shippingServiceTypeControl.valueChanges.subscribe(this.onShippingServiceTypeChange);
        this.parcelProfileControl.valueChanges.subscribe(this.onParcelProfileChange);

        if (this.parcelProfileInput) {
            this.parcelProfileInput.registerOnChange(this.onParcelProfileChange);

            if (this.fulfillmentOptionsData.parcelProfileId && this.fulfillmentOptionsData.shippingServiceType) {
                // Set the control value to the ID
                this.parcelProfileControl.setValue({ id: this.fulfillmentOptionsData.parcelProfileId }, { emitEvent: false });
                this.shippingServiceTypeControl.setValue(this.fulfillmentOptionsData.shippingServiceType, { emitEvent: false });
                this.fetchEstimationFulfillmentWeightAndCost(
                    this.fulfillmentOptionsData.parcelProfileId,
                    this.fulfillmentOptionsData.shippingServiceType,
                    this.customWeightControl.value
                );
            } else if (this.orderId) {
                this.getDefaultFulfillmentSettings(this.orderId);
            }
        }

        // Hides the auto-generated label for the fulfillmentArgs itself, as we have per-field labels.
        const parentLabel = this.formElement.nativeElement.closest('.arg-row')?.querySelector('.clr-control-label');
        if (parentLabel) {
            parentLabel.style.display = 'none';
        }
    }

    private fetchEstimationFulfillmentWeightAndCost(
        ppId: string | null,
        shippingServiceType: ShippingServiceType,
        customWeight: number | null
    ): void {
        if (!this.orderId || !ppId) { return; }
        this.setLoading(true);
        this.dataService
            .query<
                GetEstimateFulfillmentWeightAndCostQuery,
                GetEstimateFulfillmentWeightAndCostQueryVariables
            >(GET_ESTIMATE_FULFILLMENT_WEIGHT_AND_COST,
                {
                    orderId: this.orderId,
                    parcelProfileId: ppId,
                    shippingServiceType: shippingServiceType,
                    customWeight: customWeight
                })
            .mapSingle((data) => data.estimateFulfillmentWeightAndCost)
            .subscribe({
                next: (result) => {
                    if (!result) {
                        console.log('No valid estimatefor order:', this.orderId);
                        this.setEstimateError(true);
                        return;
                    }

                    this.fulfillmentOptionsData.rateId = result.rateId;
                    this.fulfillmentOptionsData.shippingServiceType = shippingServiceType;
                    this.fulfillmentOptionsData.parcelProfileId = ppId;
                    this.fulfillmentOptionsData.totalWeight = customWeight;
                    if (!customWeight) {
                        this.fulfillmentOptionsData.totalWeight = result.weight;
                    }
                    this.updateJsonString();
                    this.weightUnit = result.massUnit ?? DEFAULT_MASS_UNIT;
                    this.estimatedWeight = result.weight;
                    this.estimatedPrice = result.price;
                    this.setEstimateError(false);
                },
                error: (err) => {
                    console.error('Fetch estimation failed', err)
                    this.setEstimateError(true);
                    this.setLoading(false),
                    this.cdr.markForCheck();
                },
                complete: () => {
                    this.setLoading(false),
                    this.cdr.markForCheck();
                }
            });
    }

    private onShippingServiceTypeChange = (value: ShippingServiceType) => {
        if (this.fulfillmentOptionsData?.shippingServiceType != value) {
            this.fulfillmentOptionsData.shippingServiceType = value;
            this.fetchEstimationFulfillmentWeightAndCost(
                this.parcelProfileControl.value?.id,
                value,
                this.customWeightControl.value
            );
        }
    }

    private onCustomWeightChange = (value: number | null) => {
        if (this.fulfillmentOptionsData.totalWeight !== value) {
            this.fetchEstimationFulfillmentWeightAndCost(
                this.parcelProfileControl.value?.id,
                this.shippingServiceTypeControl.value,
                value
            );
        }
    }

    private onParcelProfileChange = (value: ParcelProfile | string | null) => {
        // Extract ID from either object or string
        let newId: string | null = null;
        if (typeof value === 'string') {
            newId = value;
        } else if (value && typeof value === 'object' && value.id) {
            newId = value.id;
        }

        if (this.fulfillmentOptionsData.parcelProfileId !== newId) {
            this.fulfillmentOptionsData.parcelProfileId = newId;
            this.updateJsonString();

            if (newId) {
                this.fetchEstimationFulfillmentWeightAndCost(
                    newId,
                    this.shippingServiceTypeControl.value,
                    this.customWeightControl.value
                );
            }
        }
    };

    private getParentPageId(): string | null {
        const key = 'id';
        let node: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
        while (node) {
            if (node.paramMap.has(key)) {
                return node.paramMap.get(key);
            }
            node = node.firstChild ?? null;
        }
        return null;
    }

    private setLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
        if (isLoading) {
          this.form.disable({ emitEvent: false });
          this.formControl.setErrors({ loading: true });
        } else {
          this.form.enable({ emitEvent: false });
          this.clearError('loading');
        }
        this.formControl.updateValueAndValidity();
    }

    private setEstimateError(estimateError: boolean): void {
        this.estimateError = estimateError;
        if (estimateError) {
            this.formControl.setErrors({ estimate: true });
        } else {
            this.clearError('estimate');
        }
        this.formControl.updateValueAndValidity();
    }

    private clearError(name: string): void {
        const err = { ...this.formControl.errors };
        delete err[name];
        this.formControl.setErrors(Object.keys(err).length ? err : null);
    }

    private updateJsonString() {
        const jsonString = JSON.stringify(this.fulfillmentOptionsData);
        this.formControl.setValue(jsonString);
        this.formControl.markAsDirty();
    }
}